import classNames from 'classnames'
import { ProductList } from '@haus-storefront-react/product-list'
import type { SearchInputProps, SearchResult } from '@haus-storefront-react/shared-types'
import { ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons'
import {
  Carousel,
  Spinner,
  Typography,
  useWidgetReadyAfterInitialLoad,
  WidgetReadyBoundary,
} from '@components'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ProductListItem from '../product-list/ProductListItem'
import type { ProductCarouselAttributes } from './_propsFn'
import styles from './ProductCarousel.module.scss'

const MOBILE_MEDIA_QUERY = '(max-width: 767px)'
const MOBILE_ITEMS_PER_VIEW = 2

interface ProductCarouselContentProps extends Pick<
  ProductCarouselAttributes,
  | 'configStyles'
  | 'itemsPerView'
  | 'showCta'
  | 'showInStock'
  | 'showPrice'
  | 'showProgressBar'
  | 'showBadges'
> {
  error: unknown
  isLoading: boolean
  products: SearchResult[]
}

/**
 * Render the carousel-backed product results for the product carousel widget.
 */
const ProductCarouselContent = ({
  configStyles,
  error,
  isLoading,
  itemsPerView,
  products,
  showCta,
  showInStock,
  showPrice,
  showBadges,
  showProgressBar,
}: ProductCarouselContentProps) => {
  const { t } = useTranslation()
  const [isMobileViewport, setIsMobileViewport] = useState(false)
  const effectiveItemsPerView = isMobileViewport
    ? Math.min(itemsPerView, MOBILE_ITEMS_PER_VIEW)
    : itemsPerView
  const shouldHideControls = products.length <= effectiveItemsPerView
  const shouldShowProgressBar = showProgressBar && !shouldHideControls
  const noProducts = !isLoading && products.length === 0
  const isReady = useWidgetReadyAfterInitialLoad({
    hasSettledContent: Boolean(error) || noProducts || products.length > 0,
    hasVisibleContent: true,
    isLoading,
  })

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY)
    const updateViewportMode = (matches: boolean) => {
      setIsMobileViewport(matches)
    }

    updateViewportMode(mediaQuery.matches)

    const handleChange = (event: MediaQueryListEvent) => {
      updateViewportMode(event.matches)
    }

    mediaQuery.addEventListener('change', handleChange)

    return () => {
      mediaQuery.removeEventListener('change', handleChange)
    }
  }, [])

  return (
    <WidgetReadyBoundary isReady={isReady}>
      <section className={styles.root} aria-label={t('a11y.productCarousel.aria.ariaLabel')}>
        {error ? (
          <div className={styles.status} role="alert">
            <Typography variant="body" className={styles.errorText}>
              {t('productCarousel.error')}
            </Typography>
          </div>
        ) : isLoading ? (
          <div className={styles.status} aria-live="polite" aria-busy="true">
            <Spinner />
          </div>
        ) : noProducts ? (
          <div className={styles.status}>
            <Typography variant="body">{t('productCarousel.empty')}</Typography>
          </div>
        ) : (
          <Carousel.Root
            itemsPerView={effectiveItemsPerView}
            ariaLabel={t('a11y.productCarousel.aria.ariaLabel')}
          >
            <Carousel.Controls
              className={classNames(styles.controls, shouldHideControls && styles.controlsHidden)}
            >
              <div className={styles.arrowControls}>
                <Carousel.Prev>
                  <ChevronLeftIcon aria-hidden width={20} height={20} />
                </Carousel.Prev>
                <Carousel.Next>
                  <ChevronRightIcon aria-hidden width={20} height={20} />
                </Carousel.Next>
              </div>
            </Carousel.Controls>

            <Carousel.Viewport>
              <Carousel.Track>
                {products.map((product) => (
                  <Carousel.Item key={product.productId}>
                    <ProductListItem
                      product={product}
                      showCta={showCta}
                      showInStock={showInStock}
                      showPrice={showPrice}
                      showBadges={showBadges}
                      configStyles={configStyles}
                    />
                  </Carousel.Item>
                ))}
              </Carousel.Track>
            </Carousel.Viewport>

            {shouldShowProgressBar && <Carousel.Progress className={styles.progressBar} />}
          </Carousel.Root>
        )}
      </section>
    </WidgetReadyBoundary>
  )
}

/**
 * Fetch products from the search endpoint and render them in a horizontal carousel.
 */
const ProductCarousel = ({
  facetValueIds,
  itemsPerView,
  productListIdentifier,
  showBadges,
  showCta,
  showInStock,
  showPrice,
  showProgressBar,
  sort,
  take,
  configStyles,
}: ProductCarouselAttributes) => {
  const { t } = useTranslation()
  const hasFacetFilters = Boolean(facetValueIds && facetValueIds.length > 0)

  if (!hasFacetFilters) {
    return (
      <section className={styles.root} aria-label={t('a11y.productCarousel.aria.ariaLabel')}>
        <div className={styles.status}>
          <Typography variant="body">{t('productCarousel.missingFacets')}</Typography>
        </div>
      </section>
    )
  }

  const searchInputProps: SearchInputProps = {
    facetValueIds,
    groupByProduct: true,
    sort,
    take,
  }

  return (
    <ProductList.Root
      productListIdentifier={productListIdentifier}
      searchInputProps={searchInputProps}
      infinitePagination={false}
    >
      {({ products, error, isLoading }) => (
        <ProductCarouselContent
          configStyles={configStyles}
          error={error}
          isLoading={isLoading}
          itemsPerView={itemsPerView}
          products={products}
          showBadges={showBadges}
          showCta={showCta}
          showInStock={showInStock}
          showPrice={showPrice}
          showProgressBar={showProgressBar}
        />
      )}
    </ProductList.Root>
  )
}

export default ProductCarousel
